import React, { useState, useContext, useEffect } from "react";

import { ReactComponent as PosBossCloud } from "assets/images/posBossCloud.svg";
import sadSoguiFaceBlack from "assets/images/sadSoguiFaceBlack.png";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import {
  Link,
  Outlet,
  useParams,
  useOutletContext,
} from "react-router-dom-v5-compat";

import { useSubmitButton } from "Atoms/Buttons";

import { ResourceStates } from "Helpers/DataDefinitions";

import { Title, ContentBox, Button } from "./Atoms";
import { BrandContext, useBrandStyleClasses } from "./BrandContext";
import Footer from "./Footer";
import { Price } from "./Item";
import { useLoadingState, LoadingSpinner } from "./Loading";
import { VenueContext } from "./VenueContext";
import { CondensedVenueHeader as VenueHeader } from "./VenueHeader";
import {
  mapCustomerTab,
  createCheckoutSession,
  fetchCustomerTab,
} from "./customerTabRequests";

const OrderItem = ({
  itemName,
  totalSalesPriceTaxInclusive,
  multiplier,
  orderItemMods,
}) => {
  const { t } = useTranslation();
  const hasNoPrice = (orderItem) => +orderItem.salesPriceTaxInclusive !== 0;

  return (
    <div>
      <div className="flex items-between">
        <div className="flex-1">
          {t("onlinePayments.orderItem", {
            count: +multiplier,
            itemName: itemName,
          })}
        </div>
        <div className="text-right">
          <Price value={totalSalesPriceTaxInclusive} />
        </div>
      </div>
      {orderItemMods.filter(hasNoPrice).map(({ name }) => (
        <div className="flex justify-between">
          <div className="pl-8">
            {t("onlinePayments.additionalMod", { modName: name })}
          </div>
        </div>
      ))}
    </div>
  );
};

export const Tab = ({}) => {
  const { t } = useTranslation();
  const { brandContrastColor } = useContext(BrandContext);
  const [submissionState, trackSubmit] = useSubmitButton();

  const { customerTabUuid } = useParams();
  const { setCustomerTab, customerTab } = useOutletContext();
  const { balance } = customerTab;

  const loadingState = useLoadingState(async () => {
    const customerTab = await fetchCustomerTab(customerTabUuid);
    setCustomerTab(customerTab);
  }, [customerTabUuid]);

  const postPayment = async () => {
    const checkoutUrl = await createCheckoutSession({
      customerTabUuid,
      taxInclusive: balance,
    });
    window.location.assign(checkoutUrl);
    await new Promise((r) => setTimeout(r, 3000));
  };

  useEffect(() => {
    const interval = setInterval(async () => {
      if (loadingState !== ResourceStates.loaded) return;

      const customerTab = await fetchCustomerTab(customerTabUuid);
      setCustomerTab(customerTab);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  if (
    loadingState === ResourceStates.loading ||
    loadingState === ResourceStates.unloaded
  ) {
    return (
      <div className="space-y-64 mx-48">
        <ContentBox className="flex items-center">
          <div className="h-48 animate-pulse my-16">
            <PosBossCloud />
          </div>
          <h3 className="text-xl">{t("common.loading")}</h3>
        </ContentBox>
      </div>
    );
  }

  if (loadingState === ResourceStates.error) {
    return (
      <div className="space-y-64 mx-48 text-center">
        <div className="flex-col items-stretch space-y-24">
          <ContentBox className="space-y-16">
            <Title>{t("errors.cantFindOrder")}</Title>
          </ContentBox>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-64 mx-48 text-center">
      <div className="flex-col items-stretch space-y-24">
        <ContentBox>
          <Title>
            {t("onlinePayments.table", { tableName: customerTab.tableName })}
          </Title>

          {balance <= 0 ? (
            customerTab.orders.length === 0 ? (
              <div className="text-xs text-neutral-600">
                {t("onlinePayments.noItems")}
              </div>
            ) : (
              <div className="flex-col justify-center space-y-12">
                <div className="text-xs text-neutral-600">
                  {t("onlinePayments.billHasBeenPaid")}
                </div>
              </div>
            )
          ) : (
            <div className="">
              <div className="text-4xl leading-tight">
                <Price value={customerTab.balance} />
              </div>
              <div className="text-xs text-neutral-600">
                {t("onlinePayments.currentOutstanding")}
              </div>
            </div>
          )}
        </ContentBox>

        <div className="space-y-12">
          {balance > 0 && (
            <Button tag={"a"} onClick={trackSubmit(postPayment)}>
              <div className="space-x-8 flex items-center">
                {submissionState === ResourceStates.loaded && (
                  <span>{t("buttons.payNow")} </span>
                )}
                {submissionState === ResourceStates.loading && (
                  <div className="space-x-8 flex items-center">
                    <LoadingSpinner
                      size={15}
                      color={brandContrastColor}
                      className="animate-spin align-middle inline-block"
                    />
                    <span>{t("buttons.loading")}</span>
                  </div>
                )}
                {submissionState === ResourceStates.error && (
                  <span>{t("buttons.error")} </span>
                )}
              </div>
            </Button>
          )}

          <Button tag={Link} secondary to="../../../" target="_blank">
            {t("buttons.viewMenu")}
          </Button>
        </div>
      </div>

      <ContentBox simple={" "}>
        <Title simple="border-b-2 border-venue-brand text-center pb-4">
          {t("onlinePayments.yourItems")}
        </Title>

        <div className="space-y-32">
          <div className="text-left space-y-12">
            {customerTab.orderItems.map((orderItem) => (
              <OrderItem {...orderItem} />
            ))}
          </div>
          <div className="text-left space-y-4">
            <div>
              <div className="flex">
                <div className="flex-1">{t("onlinePayments.total")}</div>
                <Price value={customerTab.itemTotal} />
              </div>
              {customerTab.totalHolidaySurcharges.map(
                ({ surchargeName, totalTaxInclusive }) => (
                  <div className="flex">
                    <div className="flex-1">
                      {t("onlinePayments.includesHolidaySurcharge", {
                        name: surchargeName,
                      })}
                    </div>
                    <Price value={totalTaxInclusive} />
                  </div>
                )
              )}
            </div>

            <div>
              <div className="flex">
                <div className="flex-1">{t("onlinePayments.payment")}</div>
                <Price value={customerTab.totalPayments} />
              </div>
              <div className="flex">
                <div className="flex-1">{t("onlinePayments.balanceToPay")}</div>
                <Price value={customerTab.balance} />
              </div>
            </div>
          </div>
        </div>
      </ContentBox>
    </div>
  );
};

const Tabs = () => {
  const venueBrand = useContext(BrandContext);
  const venue = useContext(VenueContext);

  const [customerTab, setCustomerTab] = useState(
    mapCustomerTab({
      orders: [],
      transactions: [],
    })
  );

  const brandStyleClasses = useBrandStyleClasses();

  return (
    <div
      className={classnames("", venueBrand.font)}
      style={venueBrand.cssVariables}
    >
      <div className="space-y-24 min-h-screen flex flex-col rounded-xl">
        <VenueHeader venue={venue} />

        <div
          className={brandStyleClasses("pb-64 py-32 flex-1", {
            simple: "rounded",
            playful: "bg-venue-foreground border-t-2 border-neutral-300",
            brutalist: "",
            elegant: "font-light",
          })}
        >
          <Outlet context={{ customerTab, setCustomerTab }} />
        </div>
      </div>
      <Footer venue={venue} />
    </div>
  );
};
export default Tabs;
